<template>
  <div
    class="table-cell-editor-listbox"
    @mousedown.stop
    @click.stop
  >
    <MSelector
      class="table-cell-editor-listbox-select"
      :model-value="selectedValue"
      :items="options"
      item-value="value"
      item-text="text"
      :placeholder="''"
      :clearable="false"
      :auto-select-first="false"
      compact
      @update:model-value="onValueUpdate"
      @user-change="onValueUpdate"
    />
    <input
      type="hidden"
      class="table-cell-editor-listbox-value"
      :value="serializedValue"
    >
  </div>
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import {
  mapListboxRowsToSelectOptions,
  resolveListboxCodeFromPageFields,
  resolveListboxOptionRowsForTableColumn,
  type ListboxSelectOption,
} from '@/common/utils/listboxTableResolve'
import { mapListboxCatalogRowsToSelectOptions } from '@/common/utils/parseListboxCatalogFromColumnScript'
import MSelector from '@/components/ui/MSelector.vue'
import { computed, ref, watch } from 'vue'

interface TableColumnLike {
  value: string
  columnElementId?: string
  listboxCatalog?: Array<{ code: string; image?: string; text?: string }>
}

interface Props {
  rowData: Record<string, any>
  column: TableColumnLike
  tableFieldName?: string
}

const props = defineProps<Props>()

const { pageFields } = usePageData()

const resolveRowFields = (): Record<string, unknown> | null => {
  const row = props.rowData?._rowData ?? props.rowData?._originalRow ?? props.rowData
  if (!row || typeof row !== 'object') return null
  return ((row as any).fields ?? row) as Record<string, unknown>
}

const listboxResolveOptions = computed(() => ({
  tableFieldName: props.tableFieldName,
  columnElementId: props.column?.columnElementId,
  rowFields: resolveRowFields(),
}))

const options = computed<ListboxSelectOption[]>(() => {
  const columnName = props.column?.value
  if (!columnName) return []

  const rows = resolveListboxOptionRowsForTableColumn(
    pageFields.value as Record<string, any> | undefined,
    columnName,
    listboxResolveOptions.value,
  )
  if (rows.length) {
    return mapListboxRowsToSelectOptions(rows)
  }

  return mapListboxCatalogRowsToSelectOptions(props.column?.listboxCatalog)
})

const unwrapCellValue = (raw: unknown): unknown => {
  if (raw != null && typeof raw === 'object' && 'value' in (raw as object)) {
    return (raw as { value: unknown }).value
  }
  return raw
}

const readCellValue = (): unknown => {
  const key = props.column?.value
  if (!key) return undefined

  const direct = props.rowData?.[key]
  if (direct !== undefined) return unwrapCellValue(direct)

  const wrapped = resolveRowFields()?.[key]
  if (wrapped !== undefined) return unwrapCellValue(wrapped)

  return undefined
}

const selectedValue = ref<unknown>(readCellValue())

watch(
  () => [props.rowData, props.column?.value, options.value.length] as const,
  () => {
    selectedValue.value = readCellValue()
  },
)

const serializeOptionValue = (value: unknown): string => {
  if (value === undefined || value === null) return ''
  return String(value)
}

const serializedValue = computed(() => serializeOptionValue(selectedValue.value))

const onValueUpdate = (value: unknown) => {
  const key = props.column?.value
  if (!key) return

  selectedValue.value = value
  props.rowData[key] = value

  const pf = pageFields.value as Record<string, any> | undefined
  const label = resolveListboxCodeFromPageFields(pf, key, value, listboxResolveOptions.value)
  if (label) {
    props.rowData[`${key}__text`] = label
  }
}
</script>

<style scoped>
.table-cell-editor-listbox {
  width: 100%;
  min-width: 120px;
}

.table-cell-editor-listbox :deep(.m-selector-wrapper),
.table-cell-editor-listbox :deep(.m-selector),
.table-cell-editor-listbox :deep(.p-select) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.table-cell-editor-listbox :deep(.p-select) {
  border: 1px solid #93c5fd;
  border-radius: 4px;
  background: #fff;
}

.table-cell-editor-listbox :deep(.p-floatlabel) {
  display: block;
  width: 100%;
}
</style>
